/** Pure helpers for Operational Journey workbench (Phase 13). UI-only heuristics — not compliance authority. */

export type EvidenceUiBucket = "complete" | "missing" | "weak" | "stale_unknown" | "not_required";

export type JourneyActionType =
  | "upload_evidence"
  | "assign_owner"
  | "create_policy"
  | "implement_control"
  | "review_exception"
  | "run_test";

export function parseEvidenceIdList(raw: string | null | undefined): string[] {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function inferActionTypes(input: {
  answerChoice: string;
  hasEvidence: boolean;
  projectionStatus?: string;
  recommendationText?: string;
}): JourneyActionType[] {
  const out = new Set<JourneyActionType>();
  const ac = String(input.answerChoice || "").toUpperCase();
  const st = String(input.projectionStatus || "").toUpperCase();
  const txt = String(input.recommendationText || "").toLowerCase();

  if (ac === "YES" || ac === "PARTIAL") {
    if (!input.hasEvidence) out.add("upload_evidence");
  }
  if (ac === "NO" || st === "NON_COMPLIANT" || st === "PARTIAL") {
    out.add("implement_control");
  }
  if (st === "PENDING_EVIDENCE" || (!input.hasEvidence && ac !== "NOT_APPLICABLE" && ac !== "NO")) {
    out.add("upload_evidence");
  }
  if (txt.includes("owner") || txt.includes("assign")) out.add("assign_owner");
  if (txt.includes("policy") || txt.includes("политик")) out.add("create_policy");
  if (txt.includes("exception") || txt.includes("исключен")) out.add("review_exception");
  if (txt.includes("test") || txt.includes("exercise") || txt.includes("drill") || txt.includes("тест")) out.add("run_test");

  if (!out.size) out.add("implement_control");
  return Array.from(out);
}

export function deriveEvidenceBucket(input: {
  answerChoice: string;
  linkedEvidenceCount: number;
  projectionCompleteness?: number | null;
}): EvidenceUiBucket {
  const ac = String(input.answerChoice || "").toUpperCase();
  if (ac === "NOT_APPLICABLE" || ac === "N/A") return "not_required";
  if (ac === "NO") return "not_required";

  const n = input.linkedEvidenceCount;
  const pct = input.projectionCompleteness == null ? null : Number(input.projectionCompleteness);

  if (n === 0) return "missing";
  if (pct != null && pct < 45) return "weak";
  if (pct != null && pct >= 75) return "complete";
  if (n > 0) return "complete";
  return "stale_unknown";
}

/** Row accent for journey question list (UI heuristic, not audit verdict). */
export type JourneyQuestionRowAccent = "neutral" | "green" | "amber" | "red";

export type JourneyRiskState = "GREEN" | "AMBER" | "RED" | "NEUTRAL";

export type LinkedEvidenceLite = {
  evidenceId?: string;
  policyId?: string;
  source?: string;
  verificationStatus?: string;
  freshnessStatus?: string;
};

export type EvidenceSufficiencyLite = {
  state: "NONE" | "PARTIAL" | "SUFFICIENT" | "STALE" | "CONFLICTED";
};

export type JourneyQuestionClassificationInput = {
  answerValue: string | null | undefined;
  evidenceIds: unknown;
  linkedEvidence?: LinkedEvidenceLite[];
  /** From session API when evidence binding is enabled. */
  evidenceSufficiencyState?: string | null;
  naJustification: string;
  projectionStatus: string | null | undefined;
  evidenceCompleteness: number | null | undefined;
  /** When true, UI shows “pending recompute” unless the row is already RED. */
  pendingRecompute?: boolean;
  standardCode?: string | null;
  articleRefs?: string[];
  evidenceModel?: Record<string, unknown>;
};

export type JourneyQuestionRowPresentation = {
  riskState: JourneyRiskState;
  accent: JourneyQuestionRowAccent;
  pendingRecompute: boolean;
  reasonCode: string | null;
  /** Full i18n key when extra explanation exists (e.g. GDPR hint). */
  reasonKey: string | null;
};

function evidenceIdCount(evidenceIds: unknown): number {
  const ev = evidenceIds;
  const arr = Array.isArray(ev) ? ev : typeof ev === "string" ? ev.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean) : [];
  return arr.length;
}

const POSITIVE_ANSWER_VALUES = new Set(["YES", "PARTIAL", "1", "2", "3", "4", "5"]);

function isVerifiedEvidenceRow(row: LinkedEvidenceLite): boolean {
  return String(row?.verificationStatus || "").toUpperCase() === "VERIFIED";
}

function isStaleEvidenceRow(row: LinkedEvidenceLite): boolean {
  const fresh = String(row?.freshnessStatus || "").toLowerCase();
  const ver = String(row?.verificationStatus || "").toUpperCase();
  return fresh === "stale" || fresh === "expired" || ver === "EXPIRED";
}

function isRejectedEvidenceRow(row: LinkedEvidenceLite): boolean {
  return String(row?.verificationStatus || "").toUpperCase() === "REJECTED";
}

function hubEvidenceRowsFromInput(input: {
  linkedEvidence?: LinkedEvidenceLite[];
  evidenceIds?: unknown;
}): LinkedEvidenceLite[] {
  const linked = Array.isArray(input.linkedEvidence) ? input.linkedEvidence : [];
  const hub = linked.filter((item) => item.evidenceId);
  if (hub.length) return hub;
  return parseResponseEvidenceIds(input.evidenceIds).map((evidenceId) => ({
    evidenceId,
    verificationStatus: "PENDING",
  }));
}

/** Deterministic mirror of backend questionEvidenceSufficiencyService (Governance Verified / Pending). */
export function evaluateLinkedEvidenceSufficiency(input: {
  answerValue?: unknown;
  linkedEvidence?: LinkedEvidenceLite[];
  evidenceIds?: unknown;
}): EvidenceSufficiencyLite {
  const av = String(input.answerValue || "").trim().toUpperCase();
  const positiveAnswer = POSITIVE_ANSWER_VALUES.has(av);
  const links = hubEvidenceRowsFromInput(input);

  if (!links.length) {
    return { state: "NONE" };
  }

  const verified = links.filter(isVerifiedEvidenceRow);
  const stale = links.filter(isStaleEvidenceRow);
  const rejected = links.filter(isRejectedEvidenceRow);

  if (rejected.length > 0 && verified.length > 0) return { state: "CONFLICTED" };
  if (rejected.length === links.length) return { state: "CONFLICTED" };
  if (stale.length > 0 && verified.length === 0) return { state: "STALE" };
  if (stale.length > 0 && verified.length > 0) return { state: "STALE" };
  if (verified.length > 0) return { state: "SUFFICIENT" };
  if (positiveAnswer) return { state: "PARTIAL" };
  return { state: "PARTIAL" };
}

export function resolveResponseEvidenceSufficiencyState(response: {
  answerValue?: unknown;
  evidenceIds?: unknown;
  linkedEvidence?: LinkedEvidenceLite[];
  evidenceSufficiency?: { state?: string } | null;
}): EvidenceSufficiencyLite["state"] {
  const fromApi = response.evidenceSufficiency?.state;
  if (fromApi) {
    const s = String(fromApi).toUpperCase();
    if (["NONE", "PARTIAL", "SUFFICIENT", "STALE", "CONFLICTED"].includes(s)) {
      return s as EvidenceSufficiencyLite["state"];
    }
  }
  return evaluateLinkedEvidenceSufficiency(response).state;
}

export function isResponseEvidenceSufficientForCompliance(response: {
  answerValue?: unknown;
  evidenceIds?: unknown;
  linkedEvidence?: LinkedEvidenceLite[];
  evidenceSufficiency?: { state?: string } | null;
}): boolean {
  return resolveResponseEvidenceSufficiencyState(response) === "SUFFICIENT";
}

export function isYesAnswerValue(answerValue: unknown): boolean {
  const av = String(answerValue ?? "").trim().toUpperCase();
  return av === "YES" || av === "TRUE" || av === "1";
}

function accentToRiskState(accent: JourneyQuestionRowAccent): JourneyRiskState {
  if (accent === "green") return "GREEN";
  if (accent === "amber") return "AMBER";
  if (accent === "red") return "RED";
  return "NEUTRAL";
}

/** Extract any control / question identifiers from a recommendation row (UI matching only). */
export function extractRecommendationControlRefs(recommendation: unknown): {
  codes: Set<string>;
  questionKeys: Set<string>;
  hasAnyIdentifier: boolean;
} {
  const codes = new Set<string>();
  const questionKeys = new Set<string>();
  const pushCode = (v: unknown) => {
    const s = v != null ? String(v).trim() : "";
    if (s) codes.add(s);
  };
  const r = (recommendation || {}) as Record<string, unknown>;
  pushCode(r.controlCode);
  pushCode(r.controlKey);
  pushCode(r.canonicalControlCode);
  pushCode(r.canonicalControlId);
  const m = r.metadata && typeof r.metadata === "object" ? (r.metadata as Record<string, unknown>) : {};
  pushCode(m.controlCode);
  pushCode(m.controlKey);
  pushCode(m.canonicalControlCode);
  const qk = m.questionKey ?? m.humanWorkflowQuestionKey ?? r.questionKey ?? r.journeyQuestionKey;
  if (qk != null) {
    const q = String(qk).trim();
    if (q) questionKeys.add(q);
  }
  const hasAnyIdentifier = codes.size > 0 || questionKeys.size > 0;
  return { codes, questionKeys, hasAnyIdentifier };
}

export type RecommendationRiskMatchMode = "none" | "matched_only" | "partial_identifiers" | "unmapped_bulk";

export type RecommendationRiskMatchSummary = {
  /** Count shown in topic cards / subtitles */
  displayCount: number;
  matchedToRisk: number;
  totalRecommendations: number;
  mode: RecommendationRiskMatchMode;
};

export function matchRecommendationsAgainstRiskSet(input: {
  riskyControlCodes: Set<string>;
  riskyQuestionKeys: Set<string>;
  recommendations: unknown[];
}): RecommendationRiskMatchSummary {
  const { riskyControlCodes, riskyQuestionKeys, recommendations } = input;
  const total = recommendations.length;
  if (!total || (!riskyControlCodes.size && !riskyQuestionKeys.size)) {
    return { displayCount: 0, matchedToRisk: 0, totalRecommendations: total, mode: "none" };
  }

  let sawAnyIdentifier = false;
  let allHaveIdentifier = true;
  let matched = 0;

  for (const rec of recommendations) {
    const { codes, questionKeys, hasAnyIdentifier } = extractRecommendationControlRefs(rec);
    if (!hasAnyIdentifier) {
      allHaveIdentifier = false;
      continue;
    }
    sawAnyIdentifier = true;
    let hit = false;
    for (const c of codes) {
      if (riskyControlCodes.has(c)) {
        hit = true;
        break;
      }
    }
    if (!hit) {
      for (const q of questionKeys) {
        if (riskyQuestionKeys.has(q)) {
          hit = true;
          break;
        }
      }
    }
    if (hit) matched += 1;
  }

  if (!sawAnyIdentifier) {
    return { displayCount: total, matchedToRisk: 0, totalRecommendations: total, mode: "unmapped_bulk" };
  }
  if (!allHaveIdentifier) {
    return { displayCount: matched, matchedToRisk: matched, totalRecommendations: total, mode: "partial_identifiers" };
  }
  return { displayCount: matched, matchedToRisk: matched, totalRecommendations: total, mode: "matched_only" };
}

export function parseGdprArticleNumbersFromRefs(refs?: string[]): Set<number> {
  const out = new Set<number>();
  for (const raw of refs || []) {
    const s = String(raw);
    const re = /(?:article|art\.?)\s*(\d+)/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(s)) != null) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) out.add(n);
    }
  }
  return out;
}

function evidenceLooksInheritedOnly(em?: Record<string, unknown>): boolean {
  if (!em) return false;
  if (em.inheritedEvidenceOnly === true) return true;
  const src = String(em.evidenceSource || em.evidenceProvenance || "").toLowerCase();
  return src.includes("inherited") || src.includes("derived");
}

function computeBaseAccent(input: JourneyQuestionClassificationInput): JourneyQuestionRowAccent {
  const av = String(input.answerValue ?? "").toLowerCase().trim();
  const hasAnswer = av.length > 0;
  if (!hasAnswer) return "neutral";

  const hubRows = hubEvidenceRowsFromInput({
    linkedEvidence: input.linkedEvidence,
    evidenceIds: input.evidenceIds,
  });
  const n = hubRows.length;
  const sufficiencyState =
    input.evidenceSufficiencyState != null
      ? String(input.evidenceSufficiencyState).toUpperCase()
      : evaluateLinkedEvidenceSufficiency({
          answerValue: input.answerValue,
          linkedEvidence: input.linkedEvidence,
          evidenceIds: input.evidenceIds,
        }).state;
  const st = String(input.projectionStatus || "").toUpperCase();
  const ec = input.evidenceCompleteness == null ? null : Number(input.evidenceCompleteness);

  if (av === "no" || av === "false" || av === "0") return "red";
  if (st === "NON_COMPLIANT") return "red";

  const na = av === "n/a" || av === "na" || av === "not_applicable";
  const naOk = String(input.naJustification || "").trim().length >= 8;
  if (na && !naOk) return "amber";

  const yes = av === "yes" || av === "true" || av === "1";
  const partialAns = av === "partial";
  if ((yes || partialAns) && n === 0) return "amber";

  if (st === "PARTIAL" || st === "PENDING_EVIDENCE") return "amber";
  if (partialAns) return "amber";

  if (na && naOk) return "green";

  if (yes && n > 0) {
    if (sufficiencyState !== "SUFFICIENT") return "amber";
    if (ec != null && ec < 45) return "amber";
    if (st === "COMPLIANT" || st === "NOT_APPLICABLE") return "green";
    if (sufficiencyState === "SUFFICIENT") return "green";
    return "amber";
  }

  return "amber";
}

function applyGdprAccentAdjustments(
  base: JourneyQuestionRowAccent,
  input: JourneyQuestionClassificationInput,
): { accent: JourneyQuestionRowAccent; reasonCode: string | null; reasonKey: string | null } {
  const std = String(input.standardCode || "").toUpperCase();
  if (std !== "GDPR") return { accent: base, reasonCode: null, reasonKey: null };

  const av = String(input.answerValue ?? "").toLowerCase().trim();
  const n = evidenceIdCount(input.evidenceIds);
  const yes = av === "yes" || av === "true" || av === "1";
  const partial = av === "partial";
  const em = input.evidenceModel;
  const articles = parseGdprArticleNumbersFromRefs(input.articleRefs);

  if ((yes || partial) && n > 0 && evidenceLooksInheritedOnly(em)) {
    const tvr = em?.targetValidationStillRequired;
    if (tvr !== false) {
      return { accent: "amber", reasonCode: "gdpr_inherited_evidence", reasonKey: "complianceCenterUi.journey.questionRow.inheritedEvidence" };
    }
  }

  const na = av === "n/a" || av === "na" || av === "not_applicable";
  if (na) {
    const j = String(input.naJustification || "").trim().length;
    const sensitiveArticle =
      [6, 7, 9].some((a) => articles.has(a)) || [...articles].some((a) => (a >= 15 && a <= 22) || (a >= 44 && a <= 49));
    if (sensitiveArticle && j < 16) {
      return { accent: "amber", reasonCode: "gdpr_na_article_sensitive", reasonKey: "complianceCenterUi.journey.questionRow.gdprNaArticleSensitive" };
    }
    if (sensitiveArticle && j >= 16 && n === 0) {
      return {
        accent: "amber",
        reasonCode: "gdpr_na_sensitive_needs_evidence",
        reasonKey: "complianceCenterUi.journey.questionRow.gdprNaSensitiveNeedsEvidence",
      };
    }
  }

  const ec = input.evidenceCompleteness == null ? null : Number(input.evidenceCompleteness);
  const highScrutinyArticle =
    articles.has(6) ||
    articles.has(7) ||
    articles.has(9) ||
    [...articles].some((a) => (a >= 15 && a <= 22) || (a >= 44 && a <= 49));
  if (yes && n > 0 && highScrutinyArticle && ec != null && ec < 75) {
    if (base === "green") {
      return {
        accent: "amber",
        reasonCode: "gdpr_sensitive_article_weak_evidence",
        reasonKey: "complianceCenterUi.journey.questionRow.gdprArticleSensitiveWeakEvidence",
      };
    }
  }

  return { accent: base, reasonCode: null, reasonKey: null };
}

export function resolveJourneyQuestionRowPresentation(input: JourneyQuestionClassificationInput): JourneyQuestionRowPresentation {
  const base = computeBaseAccent(input);
  const gdpr = applyGdprAccentAdjustments(base, input);
  let accent = gdpr.accent;
  let reasonCode = gdpr.reasonCode;
  let reasonKey = gdpr.reasonKey;

  if (input.pendingRecompute && accent !== "red") {
    accent = "amber";
    reasonCode = "pending_recompute";
    reasonKey = "complianceCenterUi.journey.questionRow.pendingRecompute";
    return {
      riskState: "AMBER",
      accent,
      pendingRecompute: true,
      reasonCode,
      reasonKey,
    };
  }

  return {
    riskState: accentToRiskState(accent),
    accent,
    pendingRecompute: false,
    reasonCode,
    reasonKey,
  };
}

export function classifyJourneyQuestionRow(input: JourneyQuestionClassificationInput): JourneyQuestionRowAccent {
  return resolveJourneyQuestionRowPresentation(input).accent;
}

export function journeyEvidenceWarnings(input: {
  answerChoice: string;
  linkedEvidenceCount: number;
  naJustification: string;
}): { yesPartialNoEvidence: boolean; naNoJustification: boolean } {
  const ac = String(input.answerChoice || "").toUpperCase();
  const yesPartial = ac === "YES" || ac === "PARTIAL";
  const yesPartialNoEvidence = yesPartial && input.linkedEvidenceCount === 0;
  const naNoJustification =
    (ac === "NOT_APPLICABLE" || ac === "N/A") && String(input.naJustification || "").trim().length < 8;
  return { yesPartialNoEvidence, naNoJustification };
}

export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

/** Label for human-workflow session `<option>` text (date-first; id fragment only as fallback). */
export type GdprEnterpriseTone = "green" | "amber" | "red";

/** Split corpus-style semicolon lists into compact UI bullets. */
export function parseSemicolonBulletList(text: string): string[] {
  return String(text || "")
    .split(/;+/)
    .map((s) => s.replace(/^[-•]\s*/, "").trim())
    .filter(Boolean);
}

/** Split remediation copy into checklist lines (semicolons or newlines). */
export function parseRemediationChecklistItems(text: string): string[] {
  const raw = String(text || "").trim();
  if (!raw) return [];
  if (raw.includes(";")) return parseSemicolonBulletList(raw);
  return raw
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Keep at most two short paragraphs for recommendation display. */
export function limitRecommendationParagraphs(text: string, maxParagraphs = 2): string[] {
  const raw = String(text || "").trim();
  if (!raw) return [];
  const byBreak = raw.split(/\n\s*\n+/).map((p) => p.trim()).filter(Boolean);
  if (byBreak.length > 1) return byBreak.slice(0, maxParagraphs);
  const sentences = raw.match(/[^.!?]+[.!?]+/g) || [raw];
  if (sentences.length <= maxParagraphs) return [raw];
  return [sentences.slice(0, maxParagraphs).join(" ").trim()];
}

export function deriveGdprAssessmentTone(input: {
  presentationAccent?: JourneyQuestionRowAccent;
  projectionStatus?: string | null;
}): GdprEnterpriseTone {
  const accent = input.presentationAccent;
  if (accent === "green") return "green";
  if (accent === "red") return "red";
  const st = String(input.projectionStatus || "").toUpperCase();
  if (st === "NON_COMPLIANT") return "red";
  if (st === "COMPLIANT" || st === "NOT_APPLICABLE") return "green";
  return "amber";
}

export function inheritanceRelationshipTone(relationship?: string | null): GdprEnterpriseTone {
  const r = String(relationship || "").toUpperCase();
  if (r.includes("SUPPORTING")) return "green";
  if (r.includes("REQUIRES") && r.includes("VALIDATION")) return "red";
  if (r.includes("SEPARATE")) return "red";
  return "amber";
}

export function inheritanceCompactBlurb(limitationsOrRationale: string, maxLen = 220): string {
  const t = String(limitationsOrRationale || "").trim();
  if (!t) return "";
  const first = t.split(/(?<=[.!?])\s+/)[0]?.trim() || t;
  return first.length > maxLen ? `${first.slice(0, maxLen).trim()}…` : first;
}

/** Exact common-problem bullets from GDPR enterprise UX spec (not corpus-generated). */
/** Exact operational evidence groupings for NIS2 enterprise workspace (UX spec). */
export const NIS2_ENTERPRISE_PRIMARY_RECORDS: Record<"en" | "ru" | "es", string[]> = {
  en: [
    "approved procedures",
    "review records",
    "operational logs",
    "escalation records",
    "management approvals",
  ],
  ru: [
    "утверждённые процедуры",
    "результаты проверок",
    "журналы операций",
    "материалы эскалации",
    "подтверждения руководства",
  ],
  es: [
    "procedimientos aprobados",
    "registros de revisión",
    "registros operativos",
    "documentación de escalado",
    "aprobaciones de dirección",
  ],
};

export const NIS2_ENTERPRISE_STRONG_INDICATORS: Record<"en" | "ru" | "es", string[]> = {
  en: [
    "recurring operational reviews",
    "assigned owners",
    "tracked remediation actions",
    "documented response activities",
    "tested recovery procedures",
  ],
  ru: [
    "регулярные проверки процессов",
    "назначенные ответственные",
    "контроль выполнения мероприятий",
    "документирование действий реагирования",
    "проверка процедур восстановления",
  ],
  es: [
    "revisiones operativas periódicas",
    "responsables asignados",
    "seguimiento de acciones correctivas",
    "actividades de respuesta documentadas",
    "procedimientos de recuperación probados",
  ],
};

export const NIS2_ENTERPRISE_WEAK_INDICATORS: Record<"en" | "ru" | "es", string[]> = {
  en: [
    "undocumented activities",
    "outdated procedures",
    "missing ownership",
    "incomplete remediation",
    "verbal-only confirmation",
  ],
  ru: [
    "действия не документируются",
    "процедуры устарели",
    "отсутствуют ответственные",
    "мероприятия не доводятся до завершения",
    "подтверждения существуют только устно",
  ],
  es: [
    "actividades sin documentación",
    "procedimientos desactualizados",
    "ausencia de responsables",
    "acciones incompletas",
    "confirmaciones solo verbales",
  ],
};

export const NIS2_ENTERPRISE_COMMON_PROBLEMS: Record<"en" | "ru" | "es", string[]> = {
  en: [
    "unresolved findings remain open",
    "operational reviews are skipped",
    "recovery procedures are not tested",
    "supplier risks are not reassessed",
    "incident actions are undocumented",
  ],
  ru: [
    "замечания остаются открытыми",
    "проверки проводятся нерегулярно",
    "восстановление не тестируется",
    "риски поставщиков не пересматриваются",
    "действия при инцидентах не фиксируются",
  ],
  es: [
    "hallazgos sin resolver",
    "revisiones omitidas",
    "recuperación no probada",
    "riesgos de proveedores sin reevaluación",
    "acciones de incidentes sin registro",
  ],
};

export const SOC2_ENTERPRISE_COMMON_PROBLEMS: Record<"en" | "ru" | "es", string[]> = {
  en: [
    "operational review older than expected cadence",
    "expired access review",
    "expired recovery test",
    "unresolved remediation older than SLA",
    "outdated incident review",
    "missing quarterly review activity",
  ],
  ru: [
    "проверка проводилась позже ожидаемого срока",
    "просрочен пересмотр доступа",
    "просрочена проверка восстановления",
    "корректирующие меры не закрыты в срок",
    "устаревший разбор инцидента",
    "отсутствует квартальная проверка",
  ],
  es: [
    "revisión operativa fuera del plazo esperado",
    "revisión de accesos vencida",
    "prueba de recuperación vencida",
    "acciones correctivas abiertas más allá del plazo",
    "revisión de incidentes desactualizada",
    "falta actividad de revisión trimestral",
  ],
};

export type Soc2EvidenceQualityLevel =
  | "HIGH_CONFIDENCE"
  | "MODERATE_CONFIDENCE"
  | "LOW_CONFIDENCE"
  | "INSUFFICIENT_EVIDENCE";

export type Soc2OperationalStatus =
  | "OPERATIONALLY_MATURE"
  | "OPERATIONALLY_PARTIAL"
  | "OPERATIONALLY_WEAK"
  | "OPERATIONALLY_UNVERIFIED";

export type Soc2RecommendationPriority =
  | "IMMEDIATE_ACTION"
  | "HIGH_PRIORITY"
  | "MODERATE_PRIORITY"
  | "ROUTINE_IMPROVEMENT";

/** UI mirror of backend soc2EvidenceQualityEngine (deterministic). */
export function deriveSoc2EvidenceQualityLevel(input: {
  answerChoice: string;
  linkedEvidenceCount: number;
  evidenceStale?: boolean;
  hasOperationalOwner?: boolean;
  hasRecurringReviewCycle?: boolean;
}): Soc2EvidenceQualityLevel {
  const ac = String(input.answerChoice || "").toUpperCase();
  const n = input.linkedEvidenceCount;
  const stale = Boolean(input.evidenceStale);
  const hasOwner = Boolean(input.hasOperationalOwner);
  const hasReview = Boolean(input.hasRecurringReviewCycle);

  if (ac === "NOT_APPLICABLE" || ac === "N/A" || !ac) return "INSUFFICIENT_EVIDENCE";
  if (n === 0 && (ac === "YES" || ac === "PARTIAL")) return "INSUFFICIENT_EVIDENCE";
  if (n === 0) return "INSUFFICIENT_EVIDENCE";
  if (stale) return "LOW_CONFIDENCE";
  if (ac === "PARTIAL" || n < 2 || !hasOwner || !hasReview) return "MODERATE_CONFIDENCE";
  if (ac === "YES" && n >= 2 && hasOwner && hasReview && !stale) return "HIGH_CONFIDENCE";
  return "LOW_CONFIDENCE";
}

/** UI mirror of backend soc2RecommendationPriorityEngine (deterministic). */
export function deriveSoc2RecommendationPriority(input: {
  answerChoice: string;
  linkedEvidenceCount: number;
  evidenceStale?: boolean;
  hasOperationalOwner?: boolean;
  unresolvedCriticalFinding?: boolean;
  operationalReviewFailed?: boolean;
  weakEvidence?: boolean;
  reviewOverdue?: boolean;
  recurringIssue?: boolean;
  incompleteRemediation?: boolean;
}): Soc2RecommendationPriority {
  const ac = String(input.answerChoice || "").toUpperCase();
  const n = input.linkedEvidenceCount;
  const stale = Boolean(input.evidenceStale);

  if (
    n === 0 ||
    input.unresolvedCriticalFinding ||
    !input.hasOperationalOwner ||
    (stale && ac === "YES") ||
    input.operationalReviewFailed
  ) {
    return "IMMEDIATE_ACTION";
  }
  if (input.weakEvidence || input.reviewOverdue || input.recurringIssue || input.incompleteRemediation) {
    return "HIGH_PRIORITY";
  }
  if (ac === "PARTIAL") return "MODERATE_PRIORITY";
  return "ROUTINE_IMPROVEMENT";
}

export function deriveSoc2OperationalStatus(input: {
  answerChoice: string;
  evidenceQuality: Soc2EvidenceQualityLevel;
  hasOperationalOwner?: boolean;
  hasOperationalReview?: boolean;
  hasRecurringReviewCycle?: boolean;
  unresolvedCriticalFinding?: boolean;
}): Soc2OperationalStatus {
  const ac = String(input.answerChoice || "").toUpperCase();
  if (!ac) return "OPERATIONALLY_UNVERIFIED";
  if (ac === "NO" || input.unresolvedCriticalFinding || !input.hasOperationalOwner) return "OPERATIONALLY_WEAK";
  if (
    input.evidenceQuality === "HIGH_CONFIDENCE" &&
    ac === "YES" &&
    input.hasOperationalReview &&
    input.hasRecurringReviewCycle
  ) {
    return "OPERATIONALLY_MATURE";
  }
  if (input.evidenceQuality === "MODERATE_CONFIDENCE" || ac === "PARTIAL") return "OPERATIONALLY_PARTIAL";
  if (input.evidenceQuality === "LOW_CONFIDENCE" || input.evidenceQuality === "INSUFFICIENT_EVIDENCE") {
    return "OPERATIONALLY_WEAK";
  }
  return "OPERATIONALLY_PARTIAL";
}

export function deriveSoc2AssessmentToneFromOperationalStatus(status: Soc2OperationalStatus): GdprEnterpriseTone {
  if (status === "OPERATIONALLY_MATURE") return "green";
  if (status === "OPERATIONALLY_WEAK" || status === "OPERATIONALLY_UNVERIFIED") return "red";
  return "amber";
}

export const GDPR_ENTERPRISE_COMMON_PROBLEMS: Record<"en" | "ru" | "es", string[]> = {
  en: [
    "documents are outdated",
    "screenshots have no timestamps",
    "approvals are missing",
    "evidence is incomplete",
    "records do not match the stated process",
    "ownership is unclear",
  ],
  ru: [
    "документы устарели",
    "на снимках экрана отсутствуют даты",
    "отсутствуют согласования",
    "подтверждающие материалы неполные",
    "записи не соответствуют описанному процессу",
    "не определены ответственные лица",
  ],
  es: [
    "los documentos están desactualizados",
    "las capturas no contienen fechas",
    "faltan aprobaciones",
    "la evidencia está incompleta",
    "los registros no coinciden con el proceso descrito",
    "no están definidos los responsables",
  ],
};

export type JourneyDomainQuestionLite = {
  questionKey: string
  canonicalControlCode?: string | null
}

export type JourneyDomainLite = {
  key: string
  questions?: JourneyDomainQuestionLite[]
}

export type JourneySessionResponseLite = {
  domainKey?: string
  questionKey?: string
  answerValue?: unknown
  evidenceIds?: unknown
  linkedEvidence?: LinkedEvidenceLite[]
  evidenceSufficiency?: { state?: string } | null
}

export type ProjectionRowLite = {
  status?: string | null
  control?: { code?: string | null }
}

export function parseResponseEvidenceIds(evidenceIds: unknown): string[] {
  if (Array.isArray(evidenceIds)) return evidenceIds.map(String).filter(Boolean)
  if (typeof evidenceIds === "string") {
    return evidenceIds
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

export function hasSavedAnswerValue(answerValue: unknown): boolean {
  return String(answerValue ?? "").trim().length > 0
}

export function isYesPartialAnswerValue(answerValue: unknown): boolean {
  const av = String(answerValue ?? "").trim().toLowerCase()
  return av === "yes" || av === "partial" || av === "true" || av === "1"
}

/** Evidence Hub / legacy evidenceIds only (policies do not close the gap metric). */
export function countEvidenceHubAttachments(response: JourneySessionResponseLite): number {
  return hubEvidenceRowsFromInput(response).length
}

/** Hub evidence verified in Governance (Evidence Hub badge: Verified). */
export function countVerifiedEvidenceHubAttachments(response: JourneySessionResponseLite): number {
  return hubEvidenceRowsFromInput(response).filter(isVerifiedEvidenceRow).length
}

export function countAnyAttachment(response: JourneySessionResponseLite): number {
  const linked = Array.isArray(response.linkedEvidence) ? response.linkedEvidence : []
  let n = 0
  for (const item of linked) {
    if (item.evidenceId || item.policyId) n += 1
  }
  if (!linked.length) n = parseResponseEvidenceIds(response.evidenceIds).length
  return n
}

export function collectControlCodesInScope(domains: JourneyDomainLite[]): Set<string> {
  const codes = new Set<string>()
  for (const d of domains) {
    for (const q of d.questions || []) {
      const code = q.canonicalControlCode ? String(q.canonicalControlCode).trim() : ""
      if (code) codes.add(code)
    }
  }
  return codes
}

/** Semantic v1 keys → bootstrap codes in human workflow catalog (mirrors backend binding). */
const SEMANTIC_TO_LEGACY_BOOTSTRAP: Record<string, string> = {
  "IAM-01": "DS-IAM-001",
  "IAM-02": "DS-IAM-001",
  "IAM-03": "DS-IAM-001",
  "IAM-04": "DS-IAM-001",
  "LOG-01": "DS-LOG-001",
  "LOG-02": "DS-LOG-001",
  "VM-01": "DS-VM-001",
  "VM-02": "DS-VM-001",
  "IR-01": "DS-IR-001",
  "BCP-01": "DS-BCM-001",
  "BKP-01": "DS-BCM-001",
  "ENC-01": "DS-DP-001",
  "ENC-02": "DS-DP-001",
  "CM-01": "DS-SDLC-001",
  "SDLC-01": "DS-SDLC-001",
  "SDLC-02": "DS-SDLC-001",
  "SEC-01": "DS-OR-001",
  "SEC-02": "DS-OR-001",
  "DATA-01": "DS-DP-001",
  "DATA-02": "DS-DP-001",
  "VRM-01": "DS-VENDOR-001",
  "AWARE-01": "DS-GOV-001",
  "RISK-01": "DS-GOV-001",
  "GOV-01": "DS-GOV-001",
  "POL-01": "DS-GOV-001",
}

/** Authoritative SOC2 codes → semantic keys (subset used for projection/journey aliasing). */
const AUTHORITATIVE_SOC2_TO_SEMANTIC: Record<string, string> = {
  "DS-SOC2-GOV-01": "GOV-01",
  "DS-SOC2-POL-01": "POL-01",
  "DS-SOC2-RISK-01": "RISK-01",
  "DS-SOC2-IAM-01": "IAM-01",
  "DS-SOC2-IAM-02": "IAM-02",
  "DS-SOC2-IAM-03": "IAM-03",
  "DS-SOC2-IAM-04": "IAM-04",
  "DS-SOC2-LOG-01": "LOG-01",
  "DS-SOC2-LOG-02": "LOG-02",
  "DS-SOC2-VM-01": "VM-01",
  "DS-SOC2-VM-02": "VM-02",
  "DS-SOC2-IR-01": "IR-01",
  "DS-SOC2-CM-01": "CM-01",
  "DS-SOC2-SDLC-01": "SDLC-01",
  "DS-SOC2-SDLC-02": "SDLC-02",
  "DS-SOC2-ENC-01": "ENC-01",
  "DS-SOC2-ENC-02": "ENC-02",
  "DS-SOC2-BKP-01": "BKP-01",
  "DS-SOC2-BCP-01": "BCP-01",
  "DS-SOC2-SEC-01": "SEC-01",
  "DS-SOC2-SEC-02": "SEC-02",
  "DS-SOC2-DATA-01": "DATA-01",
  "DS-SOC2-DATA-02": "DATA-02",
  "DS-SOC2-VRM-01": "VRM-01",
  "DS-SOC2-AWARE-01": "AWARE-01",
}

const PROJECTION_STATUS_WORST_ORDER = [
  "NON_COMPLIANT",
  "PARTIAL",
  "PENDING_EVIDENCE",
  "COMPLIANT",
  "NOT_APPLICABLE",
  "UNKNOWN",
] as const

function projectionStatusRank(status: string): number {
  const i = PROJECTION_STATUS_WORST_ORDER.indexOf(status as (typeof PROJECTION_STATUS_WORST_ORDER)[number])
  return i === -1 ? 99 : i
}

function mergeProjectionStatuses(a: string, b: string): string {
  return projectionStatusRank(a) <= projectionStatusRank(b) ? a : b
}

/** Map a projection control code onto a code present in the active journey scope, if possible. */
export function resolveScopeControlCode(rawCode: string, scopeCodes: Set<string>): string | null {
  const raw = String(rawCode || "").trim()
  if (!raw) return null
  if (scopeCodes.has(raw)) return raw

  const semantic = AUTHORITATIVE_SOC2_TO_SEMANTIC[raw]
  if (semantic && scopeCodes.has(semantic)) return semantic

  if (semantic) {
    const bootstrap = SEMANTIC_TO_LEGACY_BOOTSTRAP[semantic]
    if (bootstrap && scopeCodes.has(bootstrap)) return bootstrap
  }

  const bootstrapFromSemantic = SEMANTIC_TO_LEGACY_BOOTSTRAP[raw]
  if (bootstrapFromSemantic && scopeCodes.has(bootstrapFromSemantic)) return bootstrapFromSemantic

  return null
}

export function isNoAnswerValue(answerValue: unknown): boolean {
  const av = String(answerValue ?? "").trim().toUpperCase()
  return av === "NO" || av === "FALSE" || av === "0"
}

/**
 * Per-control rollup for session tiles: answers drive compliant / non-compliant;
 * projections supplement partial / pending when codes align.
 */
function isQuestionAnswerComplianceReady(response: JourneySessionResponseLite): boolean {
  if (!isYesAnswerValue(response.answerValue)) return false
  return isResponseEvidenceSufficientForCompliance(response)
}

export function rollupJourneyControlStatus(input: {
  responses: JourneySessionResponseLite[]
  projectionStatus?: string | null
}): string {
  const proj = String(input.projectionStatus || "").toUpperCase()
  if (proj === "NON_COMPLIANT") return "NON_COMPLIANT"

  let hasNo = false
  let yesAnswerCount = 0
  let yesCompliantCount = 0
  let hasPartialAnswer = false
  let hasYesOrPartialAnswer = false
  let hasAnyAnswer = false
  let allAnsweredAreNa = true

  for (const r of input.responses) {
    if (!hasSavedAnswerValue(r.answerValue)) continue
    hasAnyAnswer = true
    const av = String(r.answerValue).trim().toUpperCase()
    if (isNoAnswerValue(r.answerValue)) hasNo = true
    if (av !== "NOT_APPLICABLE" && av !== "N/A") allAnsweredAreNa = false
    if (av === "PARTIAL") hasPartialAnswer = true
    if (isYesPartialAnswerValue(r.answerValue)) {
      hasYesOrPartialAnswer = true
      if (isYesAnswerValue(r.answerValue)) {
        yesAnswerCount += 1
        if (isQuestionAnswerComplianceReady(r)) yesCompliantCount += 1
      }
    }
  }

  if (hasNo) return "NON_COMPLIANT"
  if (
    proj === "COMPLIANT" ||
    (yesAnswerCount > 0 && yesCompliantCount === yesAnswerCount && !hasPartialAnswer)
  ) {
    return "COMPLIANT"
  }
  if ((proj === "NOT_APPLICABLE" || (allAnsweredAreNa && hasAnyAnswer)) && !hasYesOrPartialAnswer) {
    return "NOT_APPLICABLE"
  }
  if (proj === "PARTIAL" || hasYesOrPartialAnswer || hasPartialAnswer) return "PARTIAL"
  if (proj === "PENDING_EVIDENCE" || !hasAnyAnswer) return "PENDING_EVIDENCE"
  return "PENDING_EVIDENCE"
}

function buildScopedProjectionStatusMap(
  scopeCodes: Set<string>,
  projections: ProjectionRowLite[],
): Map<string, string> {
  const map = new Map<string, string>()
  for (const p of projections) {
    const raw = String(p.control?.code ?? "").trim()
    const scopeCode = resolveScopeControlCode(raw, scopeCodes)
    if (!scopeCode) continue
    const status = String(p.status || "UNKNOWN").toUpperCase()
    const prev = map.get(scopeCode)
    map.set(scopeCode, prev ? mergeProjectionStatuses(prev, status) : status)
  }
  return map
}

function countControlsByRollupStatus(
  scopeCodes: Set<string>,
  responsesByControl: Map<string, JourneySessionResponseLite[]>,
  projectionByScope: Map<string, string>,
): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const code of scopeCodes) {
    const status = rollupJourneyControlStatus({
      responses: responsesByControl.get(code) || [],
      projectionStatus: projectionByScope.get(code),
    })
    counts[status] = (counts[status] || 0) + 1
  }
  return counts
}

export type JourneyWorkbenchStats = {
  totalQ: number
  answered: number
  unanswered: number
  evidenceLinkedAnswers: number
  evidenceMissingAnswers: number
  compliant: number
  partial: number
  nonCompliant: number
  pendingEvidence: number
  notApplicable: number
}

/**
 * Session-scoped journey metrics: answers from workflow responses; projection tiles only for
 * canonical controls in the active standard's journey domains (not whole org catalog).
 */
export function computeJourneyWorkbenchStats(input: {
  domains: JourneyDomainLite[]
  responses: JourneySessionResponseLite[]
  projections: ProjectionRowLite[]
}): JourneyWorkbenchStats {
  const { domains, responses, projections } = input
  const totalQ = domains.reduce((acc, d) => acc + (d.questions?.length || 0), 0)

  const questionToControl = new Map<string, string>()
  for (const d of domains) {
    for (const q of d.questions || []) {
      questionToControl.set(`${d.key}::${q.questionKey}`, String(q.canonicalControlCode || "").trim())
    }
  }

  const answeredKeys = new Set<string>()
  let evidenceLinkedAnswers = 0
  let evidenceMissingAnswers = 0
  const answeredControlCodes = new Set<string>()

  for (const r of responses) {
    const composite = `${String(r.domainKey ?? "")}::${String(r.questionKey ?? "")}`
    if (!r.questionKey || !hasSavedAnswerValue(r.answerValue)) continue
    answeredKeys.add(composite)

    const controlCode = questionToControl.get(composite)
    if (controlCode) answeredControlCodes.add(controlCode)

    if (isYesPartialAnswerValue(r.answerValue)) {
      const anyAttachment = countAnyAttachment(r)
      if (anyAttachment > 0) evidenceLinkedAnswers += 1
      if (!isResponseEvidenceSufficientForCompliance(r)) evidenceMissingAnswers += 1
    }
  }

  const codesInScope = collectControlCodesInScope(domains)
  const responsesByControl = new Map<string, JourneySessionResponseLite[]>()
  for (const r of responses) {
    const composite = `${String(r.domainKey ?? "")}::${String(r.questionKey ?? "")}`
    const controlCode = questionToControl.get(composite)
    if (!controlCode || !codesInScope.has(controlCode)) continue
    const list = responsesByControl.get(controlCode) || []
    list.push(r)
    responsesByControl.set(controlCode, list)
  }

  const projectionByScope = buildScopedProjectionStatusMap(codesInScope, projections)
  const byStatus = countControlsByRollupStatus(codesInScope, responsesByControl, projectionByScope)

  return {
    totalQ,
    answered: answeredKeys.size,
    unanswered: Math.max(0, totalQ - answeredKeys.size),
    evidenceLinkedAnswers,
    evidenceMissingAnswers,
    compliant: byStatus.COMPLIANT || 0,
    partial: byStatus.PARTIAL || 0,
    nonCompliant: byStatus.NON_COMPLIANT || 0,
    pendingEvidence: byStatus.PENDING_EVIDENCE || 0,
    notApplicable: byStatus.NOT_APPLICABLE || 0,
  }
}

export function formatHumanWorkflowSessionOptionLabel(
  row: {
    id?: string;
    createdAt?: string | null;
    updatedAt?: string | null;
    navigationMode?: string | null;
    standardCode?: string | null;
  },
  standardLabel: string,
): string {
  const when = formatIsoDate(row.createdAt || row.updatedAt);
  const mode = row.navigationMode ? String(row.navigationMode).toUpperCase() : "";
  const std = standardLabel?.trim() || "";
  if (when) {
    return [when, mode, std].filter(Boolean).join(" · ");
  }
  const id = row.id ? `${String(row.id).slice(0, 8)}…` : "";
  return [id || "—", mode, std].filter(Boolean).join(" · ");
}
